import React, {useCallback, useMemo, useState} from 'react';
import {Box} from "@mui/material";
import {IntegrationsComponent} from "./IntegrationsComponent";
import {useQueryWithAccessToken} from "../../../../hooks/TanstackHooks";
import {cacheKeys} from "../../../../services/shared/serviceConstants";
import {useComponentForQueryResult} from "../../../../hooks/UseComponentForQueryResult";
import {getAllClients} from "../../../../services/Clients/ClientsQueries";
import {getSaasClientRestrictions} from "../../../../services/SaasRestrictions/SaasRestrictionsQueries";
import {DefaultTableContainer} from "../../../../components/common/DefaultTable";

// Super admin only: which clients each SaaS integration is available to
export const IntegrationsPanel = ({ userRole }:{ userRole: string }) => {
  const documentTitle = 'Integrations - Detexian';
  document.title = documentTitle;

  const [userErrorMessage, setUserErrorMessge] = useState('');

  const integrationsQueryResult = useQueryWithAccessToken({
      queryKey: [cacheKeys.saasClientRestrictions],
      queryFunction: getSaasClientRestrictions,
      queryFunctionVariables: {},
      tanstackOptions:{
        onError: () => {
          setUserErrorMessge('error accessing integrations');
        }
      },
      userRole
    }
  );

  // The same query as the Clients panel, for the client names
  const clientsQueryResult = useQueryWithAccessToken({
      queryKey: [cacheKeys.clients],
      queryFunction: getAllClients,
      queryFunctionVariables: {},
      tanstackOptions:{
        onError: () => {
          setUserErrorMessge('error accessing clients');
        }
      },
      userRole
    }
  );

  // useComponentForQueryResult supports one query, so combine both
  const combinedQueryResult = useMemo(() => ({
    isError: integrationsQueryResult.isError || clientsQueryResult.isError,
    isLoading: integrationsQueryResult.isLoading || clientsQueryResult.isLoading,
    isSuccess: integrationsQueryResult.isSuccess && clientsQueryResult.isSuccess,
    error: integrationsQueryResult.error ?? clientsQueryResult.error,
    data: integrationsQueryResult.isSuccess && clientsQueryResult.isSuccess ?
      {integrations: integrationsQueryResult.data, clients: clientsQueryResult.data} :
      undefined,
  }), [
    integrationsQueryResult.isError, integrationsQueryResult.isLoading,
    integrationsQueryResult.isSuccess, integrationsQueryResult.error, integrationsQueryResult.data,
    clientsQueryResult.isError, clientsQueryResult.isLoading,
    clientsQueryResult.isSuccess, clientsQueryResult.error, clientsQueryResult.data,
  ]);

  // builds the successful page for the hook below
  const pageLoadFunction = useCallback(
    () =>
      <IntegrationsComponent
        userRole={userRole}
        integrations={integrationsQueryResult.data}
        clients={clientsQueryResult.data}
      />
    ,
    [integrationsQueryResult.data, clientsQueryResult.data, userRole]
  );

  return <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      minWidth: '1000px',
    }}
  >
    <DefaultTableContainer
      shouldUseTableContainer={false}
    >
      {useComponentForQueryResult({
        queryResult: combinedQueryResult,
        pageLoadFunction,
        userErrorMessage,
        errorMessageDoesIndicateError: true
      })}
    </DefaultTableContainer>
  </Box>
};
